import { X } from 'lucide-react'
import { useState } from 'react'
import { formatCents, parseMoney } from '../../api/parseMoney'
import { Icon } from '../../platform/icons/Icon'
import { Button } from '../../platform/primitives/Button'
import { Field } from '../../platform/primitives/Field'
import { Select } from '../../platform/primitives/Select'
import type { TxnFilters } from './filters'
import { monthName } from './txnDisplay'
import type { FilterOptions } from './useFilterOptions'

/** What a control reports: only its own fields. The caller resets the page. */
export type Patch = Partial<TxnFilters>

export const MONTHS_BACK = 24

/** "2026-07", this month first, back `MONTHS_BACK` months. Built from the local calendar month: the default month is today's. */
export function monthChoices(today: Date, current: string | null): { value: string; label: string }[] {
  const out: { value: string; label: string }[] = [{ value: 'all', label: 'All time' }]
  for (let i = 0; i <= MONTHS_BACK; i++) {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1)
    const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`
    out.push({ value, label: monthLabel(value) })
  }
  // A month the URL names that is older than the list: keep it selectable, or the select would show a value it does not offer.
  if (current !== null && !out.some((o) => o.value === current)) out.push({ value: current, label: monthLabel(current) })
  return out
}

function monthLabel(month: string): string {
  const year = month.slice(0, 4)
  const name = monthName(month)
  return `${name[0]?.toUpperCase() ?? ''}${name.slice(1)} ${year}`
}

/** Month, account, category and owner: the four selects that sit in the bar, and in the phone sheet. */
export function PrimarySelects({
  filters,
  options,
  today,
  onPatch,
}: {
  filters: TxnFilters
  options: FilterOptions
  today: Date
  onPatch: (p: Patch) => void
}) {
  return (
    <>
      <Select
        className="txn-filters__select"
        label="Month"
        value={filters.month ?? 'all'}
        onChange={(e) => onPatch({ month: e.target.value === 'all' ? null : e.target.value })}
      >
        {monthChoices(today, filters.month).map((m) => (
          <option key={m.value} value={m.value}>
            {m.label}
          </option>
        ))}
      </Select>
      <Select
        className="txn-filters__select"
        label="Account"
        value={filters.accountId ?? ''}
        onChange={(e) => onPatch({ accountId: e.target.value || null })}
      >
        <option value="">All accounts</option>
        {options.accounts.map((a) => (
          <option key={a.id} value={a.id}>
            {a.label}
          </option>
        ))}
      </Select>
      <Select
        className="txn-filters__select"
        label="Category"
        value={filters.categoryId ?? ''}
        onChange={(e) => onPatch({ categoryId: e.target.value || null })}
      >
        {/* No "Needs category" entry (Q14): that is a row's state, not a server filter. */}
        <option value="">All categories</option>
        {options.categories.map((g) => (
          <optgroup key={g.group} label={g.group}>
            {g.items.map((c) => (
              <option key={c.id} value={c.id}>
                {c.label}
              </option>
            ))}
          </optgroup>
        ))}
      </Select>
      <Select
        className="txn-filters__select"
        label="Owner"
        value={filters.ownerId ?? ''}
        onChange={(e) => onPatch({ ownerId: e.target.value || null })}
      >
        <option value="">Anyone</option>
        {options.owners.map((o) => (
          <option key={o.id} value={o.id}>
            {o.label}
          </option>
        ))}
      </Select>
    </>
  )
}

/** Tag, spend range and pending: what "More filters" holds, on desktop and in the phone sheet. */
export function MoreFields({
  filters,
  options,
  onPatch,
}: {
  filters: TxnFilters
  options: FilterOptions
  onPatch: (p: Patch) => void
}) {
  return (
    <div className="txn-more">
      <Select
        label="Tag"
        value={filters.tag ?? ''}
        onChange={(e) => onPatch({ tag: e.target.value || null })}
      >
        <option value="">Any tag</option>
        {/* A tag the URL names that the list does not offer stays selectable. */}
        {filters.tag !== null && !options.tags.includes(filters.tag) && <option value={filters.tag}>{filters.tag}</option>}
        {options.tags.map((t) => (
          <option key={t} value={t}>
            {t}
          </option>
        ))}
      </Select>
      <SpendRange min={filters.spentMin} max={filters.spentMax} onCommit={(min, max) => onPatch({ spentMin: min, spentMax: max })} />
      <Select
        label="Pending"
        value={filters.pending === null ? '' : String(filters.pending)}
        onChange={(e) => onPatch({ pending: e.target.value === '' ? null : e.target.value === 'true' })}
      >
        <option value="">Pending: any</option>
        <option value="true">Pending only</option>
        <option value="false">Posted only</option>
      </Select>
    </div>
  )
}

type SpendErrors = { min?: string; max?: string; range?: string }

const textOf = (cents: number | null) => (cents === null ? '' : formatCents(cents))

/**
 * "Spent between": two money fields, expense-positive (Q14).
 *
 * Committed on blur or Enter rather than on every keystroke, so "12" on the way
 * to "12.50" never reaches the URL. **Invalid input writes nothing**: it is
 * shown as an error on its field and the last good range stays. The text is
 * parsed with `parseMoney`, which refuses a third decimal, a symbol or a
 * separator; a negative is refused here, since a "spent" amount is not signed.
 */
function SpendRange({
  min,
  max,
  onCommit,
}: {
  min: number | null
  max: number | null
  onCommit: (min: number | null, max: number | null) => void
}) {
  const [texts, setTexts] = useState({ min: textOf(min), max: textOf(max) })
  const [errors, setErrors] = useState<SpendErrors>({})
  // Reset from the URL when it moves under us (Clear all), compared during
  // render rather than in an effect, which would paint one stale frame.
  const [seen, setSeen] = useState({ min, max })
  if (seen.min !== min || seen.max !== max) {
    setSeen({ min, max })
    setTexts({ min: textOf(min), max: textOf(max) })
    setErrors({})
  }

  function parse(text: string): { cents: number | null } | { error: string } {
    const t = text.trim()
    if (t === '') return { cents: null }
    try {
      const cents = parseMoney(t)
      return cents < 0 ? { error: 'Enter an amount of zero or more' } : { cents }
    } catch {
      return { error: 'Enter an amount like 12.50' }
    }
  }

  function commit(next: { min: string; max: string }) {
    const a = parse(next.min)
    const b = parse(next.max)
    const found: SpendErrors = {}
    if ('error' in a) found.min = a.error
    if ('error' in b) found.max = b.error
    if (!('error' in a) && !('error' in b) && a.cents !== null && b.cents !== null && a.cents > b.cents) {
      found.range = 'Minimum is above maximum'
    }
    setErrors(found)
    if (found.min || found.max || found.range) return
    const lo = 'cents' in a ? a.cents : null
    const hi = 'cents' in b ? b.cents : null
    if (lo !== min || hi !== max) onCommit(lo, hi)
  }

  return (
    <fieldset className="txn-spend">
      <legend className="txn-spend__legend">Spent between</legend>
      <Field
        label="Minimum spend"
        inputMode="decimal"
        value={texts.min}
        error={errors.min ?? errors.range}
        onChange={(e) => setTexts({ ...texts, min: e.target.value })}
        onBlur={() => commit(texts)}
        onKeyDown={(e) => e.key === 'Enter' && commit(texts)}
      />
      <Field
        label="Maximum spend"
        inputMode="decimal"
        value={texts.max}
        error={errors.max}
        onChange={(e) => setTexts({ ...texts, max: e.target.value })}
        onBlur={() => commit(texts)}
        onKeyDown={(e) => e.key === 'Enter' && commit(texts)}
      />
    </fieldset>
  )
}

/**
 * The "Needs review" toggle. `aria-pressed` carries the state, the words carry
 * the meaning, and the pressed chip is an olive-tint pill outlined in
 * `--accent-ink` with an ✕, so colour is never the only signal. The tint is the
 * selection role (rule 9), not a primary action.
 */
/** What the sheet hands a connected chip: the month being drafted and the draft's toggle. */
export type ReviewChipSlotProps = { month: string | null; pressed: boolean; onToggle: () => void }

export function ReviewChip({
  pressed,
  count,
  onToggle,
}: {
  pressed: boolean
  count: number | undefined
  onToggle: () => void
}) {
  // Nothing to review and not filtering: the chip would be a dead control.
  if (!pressed && (count === undefined || count === 0)) return null
  return (
    <Button
      type="button"
      size="page"
      variant={pressed ? 'selected' : 'outline'}
      aria-pressed={pressed}
      onClick={onToggle}
    >
      Needs review{count !== undefined ? ` ${count}` : ''}
      {pressed && <Icon glyph={X} size={13} />}
    </Button>
  )
}
